import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import type { RouteHandle } from '@/routes/types';
import { Fragment, type ReactNode } from 'react';
import { Link, useMatches } from 'react-router-dom';

interface Crumb {
  key: string;
  label: ReactNode;
  to: string;
}

/**
 * Renders the breadcrumb trail for the currently matched route chain.
 * Each crumb is sourced from the route's `handle.crumb` — either a
 * static string or a function of the matched params (so a dynamic
 * detail route like `/catalog/:slug` can render the slug). The last
 * matched crumb renders as a non-link "current page" indicator; earlier
 * crumbs render as router `<Link>`s.
 *
 * Pulling labels off the route handle keeps the breadcrumb component
 * decoupled from page titles: a feature can rename itself by editing
 * one line in its `*.routes.tsx` file. Pages don't need to know they
 * appear in a breadcrumb.
 */
export function Breadcrumbs() {
  const matches = useMatches();
  const crumbs: Crumb[] = matches
    .map((match) => {
      const handle = match.handle as RouteHandle | null | undefined;
      if (handle?.crumb === undefined) return null;
      const label = typeof handle.crumb === 'function' ? handle.crumb(match.params) : handle.crumb;
      return { key: match.id, label, to: handle.crumbTo ?? match.pathname };
    })
    .filter((c): c is Crumb => c !== null);

  if (crumbs.length === 0) return null;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            <Fragment key={crumb.key}>
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.to}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast ? <BreadcrumbSeparator /> : null}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
